import React from 'react';

import {
  Checkbox,
  TextInput,
  useMantineTheme,
  Text,
  Tooltip,
  ActionIcon,
  px,
  Button,
  Space,
  Title,
  Paper,
  createStyles,
  Select,
} from '@mantine/core';
import { IconFilterOff, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useListStaff from 'hooks/staff/useListStaff';
import { StaffList } from 'types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'auto 300px max-content max-content min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title search login lastName clear download button"`,
    alignItems: 'center',
    gap: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto max-content max-content min-content min-content min-content',
      gridTemplateAreas: `
         'title title title button button button'
         'search login lastName  . clear download'
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto max-content max-content min-content min-content min-content',
      gridTemplateAreas: `
         'title title title button button button'
         'search search search search search search'
         '. login lastName . clear download'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  lastName: {
    gridArea: 'lastName',
  },
  search: {
    gridArea: 'search',
  },
  login: {
    gridArea: 'login',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
  },
  button: {
    gridArea: 'button',
  },
}));

const csvOptions = {
  filename: 'staff-list',
  fieldSeparator: ',',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: false,
  headers: ['Name', 'Coach', 'Login', 'Email', 'Phone', 'Date of Birth'],
};

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  loginEnabled: string;
}
const StaffPageHeader = ({
  searchFilters,
  initialSearchFiltersValue,
  setSearchFilters,
  handleOpenCreateForm,
  orderingValue,
  permission = true,
}: {
  searchFilters: FilterTerms;
  initialSearchFiltersValue: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
  orderingValue: string;
  handleOpenCreateForm: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();

  const dataToRow = (staff: StaffList): { [key: string]: any } => ({
    Name: staff.fullName,
    Coach: staff.isCoach ? 'Yes' : 'No',
    Login: staff.loginEnabled ? 'Yes' : 'No',
    Email: staff.email,
    Phone: staff.phone || '',
    'Date of Birth': staff.dob ? dayjs(staff.dob).format(dateFormat) : '',
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: value }));
  };
  const handleIsArrangeByLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilters(prevValue => ({ ...prevValue, isArrangeByLastName: !!e.currentTarget?.checked }));
  };
  const handleLoginStatusChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, loginEnabled: value || '' }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: '' }));
  };

  return (
    <Paper className={classes.header}>
      <Title order={1} size='h1' pr='xl' className={classes.title}>
        Staff
      </Title>
      <Checkbox
        className={classes.lastName}
        checked={searchFilters.isArrangeByLastName}
        onChange={handleIsArrangeByLastNameChange}
        label={
          <Text color='gray' fs='italic'>
            Arrange by last name
          </Text>
        }
      />
      <TextInput
        className={classes.search}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by name, email, phone'
        value={searchFilters?.searchTerm}
        onChange={handleSearchChange}
        rightSection={
          !!searchFilters.searchTerm ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Select
        className={classes.login}
        placeholder='Select login status'
        value={searchFilters?.loginEnabled}
        onChange={handleLoginStatusChange}
        data={[
          { label: 'Login Enabled', value: 'true' },
          { label: 'Login Disabled', value: 'false' },
        ]}
        clearable
      />
      <Tooltip label='Clear filters' offset={px(theme.spacing.md)} className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={useListStaff}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{ ...searchFilters, ordering: orderingValue }}
        className={classes.download}
        reportType='staff-export'
      />

      <Button
        onClick={handleOpenCreateForm}
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        className={classes.button}
        disabled={!permission}
        data-cy='addStaffButton'
      >
        Staff
      </Button>
    </Paper>
  );
};

export default StaffPageHeader;
